import React, { cloneElement } from 'react'
import PropTypes from 'prop-types'
import { Label } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import FieldTooltip from './fieldTooltip'
import FieldLabel from './fieldLabel'
import CheckboxLabel from './checkboxLabel'
import SubLabel from './subLabel'
import styles from './styles.scss'
const cx = classnames.bind(styles)

/**
 * New label approach introduced early 2022 to show a label with subLabel help text
 * - For callers that want to pass more than just a label string, they can use this and pass it in.
 * - This will automatically handle checkbox/radio buttons as well (when checkboxType is specified)
 */
const StandardFieldLabel = ({
  label,
  subLabel,
  // these remaining props below should come from parent form field wrapper
  checkboxType,
  compact = false,
  hideLabel = false,
  showAnchor = false,
  showErrorAbove,
  helpContent,
  error,
  showError = false,
  warning,
  inputName,
  required,
  fieldFocused,
  elmId,
  className,
  labelEllipsis,
  pendingApproval,
  tooltipIconStyle,
}) => {
  const fieldLabelProps = {
    label,
    className,
    elmId,
    inputName,
    error,
    showError,
    required,
    pendingApproval,
  }

  const fieldTooltipProps = {
    label,
    helpContent,
    showError: showErrorAbove,
    error,
    showAnchor,
    elmId,
    iconStyle: tooltipIconStyle,
  }

  return checkboxType ? (
    <CheckboxLabel
      {...{
        label,
        subLabel,
        compact,
        elmId,
        checkboxType,
        showAnchor,
        helpContent,
      }}
    />
  ) : (
    <>
      <div
        className={cx('label-tooltip-container', {
          compact,
          'label-ellipsis': labelEllipsis,
        })}
      >
        {!hideLabel && <FieldLabel {...fieldLabelProps} />}
        {(showAnchor || showErrorAbove) && (
          <FieldTooltip {...fieldTooltipProps} />
        )}
      </div>
      <SubLabel label={subLabel} compact={compact} />
      {warning && (
        <Label
          basic
          className={`warning-label ${fieldFocused ? '' : 'hidden'}`}
          pointing='below'
        >
          {warning}
        </Label>
      )}
    </>
  )
}

StandardFieldLabel.propTypes = {
  label: PropTypes.node.isRequired,
  subLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  checkboxType: PropTypes.oneOf(['checkbox', 'radio']),
  compact: PropTypes.bool,
  hideLabel: PropTypes.bool,
  showAnchor: PropTypes.bool,
  showErrorAbove: PropTypes.bool,
  helpContent: PropTypes.node,
  error: PropTypes.string,
  showError: PropTypes.bool,
  warning: PropTypes.string,
  inputName: PropTypes.string,
  required: PropTypes.bool,
  fieldFocused: PropTypes.bool,
  pendingApproval: PropTypes.bool,
  elmId: PropTypes.string,
  className: PropTypes.string,
  labelEllipsis: PropTypes.bool,
  tooltipIconStyle: PropTypes.string,
}

// helper for creating a StandardFieldLabel from a label
StandardFieldLabel.createFromLabel = (label, labelProps) =>
  label?.type?.createFromLabel ? ( // we assume it's a standardFieldLabel when it has this fn attached
    cloneElement(label, labelProps)
  ) : (
    <StandardFieldLabel label={label} {...labelProps} />
  )

export default StandardFieldLabel
